import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { formatDateDDMMYYYY, formatCurrency } from "@/lib/utils";
import { TransactionTypeBadge } from "@/components/transactions/transaction-type-badge";
import { TransactionStatusBadge } from "@/components/transactions/transaction-status-badge";
import { Badge } from "@/components/ui/badge";
import type { Transaction, ColumnVisibility } from "@/types/transaction";

type SortColumn = keyof Transaction | null;
type SortDirection = "asc" | "desc";

interface TransactionTableProps {
    transactions: Transaction[];
    columnVisibility: ColumnVisibility;
    onViewTransaction?: (transaction: Transaction) => void;
    showCustomerColumn?: boolean;
    enableSelection?: boolean;
    selectedRows?: Set<number>;
    onSelectRow?: (id: number, checked: boolean) => void;
    onSelectAll?: (checked: boolean) => void;
    currentPage?: number;
    pageSize?: number;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
}

export function TransactionTable({
    transactions,
    columnVisibility,
    onViewTransaction,
    showCustomerColumn = true,
    enableSelection = false,
    selectedRows = new Set(),
    onSelectRow,
    onSelectAll,
    currentPage = 1,
    pageSize = 25,
    onPageChange,
    onPageSizeChange,
}: TransactionTableProps) {
    const navigate = useNavigate();
    const [sortColumn, setSortColumn] = useState<SortColumn>("transactionDate");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    // Sort transactions
    const sortedTransactions = useMemo(() => {
        if (!sortColumn) return transactions;

        return [...transactions].sort((a, b) => {
            const aValue = a[sortColumn];
            const bValue = b[sortColumn];

            if (aValue === undefined || aValue === null) return 1;
            if (bValue === undefined || bValue === null) return -1;

            let comparison = 0;
            if (typeof aValue === "string" && typeof bValue === "string") {
                comparison = aValue.localeCompare(bValue);
            } else if (typeof aValue === "number" && typeof bValue === "number") {
                comparison = aValue - bValue;
            }

            return sortDirection === "asc" ? comparison : -comparison;
        });
    }, [transactions, sortColumn, sortDirection]);

    // Paginate
    const paginatedTransactions = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return sortedTransactions.slice(startIndex, startIndex + pageSize);
    }, [sortedTransactions, currentPage, pageSize]);

    const totalPages = Math.ceil(sortedTransactions.length / pageSize);

    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    const handleSelectAll = (checked: boolean | "indeterminate") => {
        if (onSelectAll && checked === true) {
            onSelectAll(true);
        } else if (onSelectAll) {
            onSelectAll(false);
        }
    };

    const handleSelectRow = (id: number, checked: boolean | "indeterminate") => {
        if (onSelectRow && checked === true) {
            onSelectRow(id, true);
        } else if (onSelectRow) {
            onSelectRow(id, false);
        }
    };

    return (
        <>
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {enableSelection && (
                                <TableHead className="w-12">
                                    <Checkbox
                                        checked={
                                            paginatedTransactions.length > 0 &&
                                            paginatedTransactions.every((t) => selectedRows.has(t.id))
                                        }
                                        onCheckedChange={handleSelectAll}
                                    />
                                </TableHead>
                            )}
                            {columnVisibility.referenceNumber && (
                                <TableHead
                                    className="cursor-pointer"
                                    onClick={() => handleSort("referenceNumber")}
                                >
                                    <div className="flex items-center gap-1">
                                        Reference Number
                                        {sortColumn === "referenceNumber" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.transactionDate && (
                                <TableHead
                                    className="cursor-pointer"
                                    onClick={() => handleSort("transactionDate")}
                                >
                                    <div className="flex items-center gap-1">
                                        Transaction Date
                                        {sortColumn === "transactionDate" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.dueDate && (
                                <TableHead
                                    className="cursor-pointer"
                                    onClick={() => handleSort("dueDate")}
                                >
                                    <div className="flex items-center gap-1">
                                        Due Date
                                        {sortColumn === "dueDate" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {showCustomerColumn && columnVisibility.customerName && (
                                <TableHead
                                    className="cursor-pointer"
                                    onClick={() => handleSort("customerName")}
                                >
                                    <div className="flex items-center gap-1">
                                        Customer Name
                                        {sortColumn === "customerName" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.transactionType && (
                                <TableHead
                                    className="cursor-pointer"
                                    onClick={() => handleSort("transactionType")}
                                >
                                    <div className="flex items-center gap-1">
                                        Type
                                        {sortColumn === "transactionType" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.amount && (
                                <TableHead
                                    className="cursor-pointer text-right"
                                    onClick={() => handleSort("amount")}
                                >
                                    <div className="flex items-center justify-end gap-1">
                                        Gross
                                        {sortColumn === "amount" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.outstandingAmount && (
                                <TableHead
                                    className="cursor-pointer text-right"
                                    onClick={() => handleSort("outstandingAmount")}
                                >
                                    <div className="flex items-center justify-end gap-1">
                                        Outstanding
                                        {sortColumn === "outstandingAmount" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.status && (
                                <TableHead
                                    className="cursor-pointer"
                                    onClick={() => handleSort("status")}
                                >
                                    <div className="flex items-center gap-1">
                                        Status
                                        {sortColumn === "status" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.description && <TableHead>Description</TableHead>}
                            {columnVisibility.createdBy && <TableHead>Created By</TableHead>}
                            {columnVisibility.modifiedBy && <TableHead>Modified By</TableHead>}
                            {columnVisibility.accountCode && (
                                <TableHead
                                    className="cursor-pointer"
                                    onClick={() => handleSort("accountCode")}
                                >
                                    <div className="flex items-center gap-1">
                                        Account
                                        {sortColumn === "accountCode" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.netAmount && (
                                <TableHead
                                    className="cursor-pointer text-right"
                                    onClick={() => handleSort("netAmount")}
                                >
                                    <div className="flex items-center justify-end gap-1">
                                        Net
                                        {sortColumn === "netAmount" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.vatAmount && (
                                <TableHead
                                    className="cursor-pointer text-right"
                                    onClick={() => handleSort("vatAmount")}
                                >
                                    <div className="flex items-center justify-end gap-1">
                                        Tax
                                        {sortColumn === "vatAmount" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.customerReference && (
                                <TableHead
                                    className="cursor-pointer"
                                    onClick={() => handleSort("customerReference")}
                                >
                                    <div className="flex items-center gap-1">
                                        Customer Reference
                                        {sortColumn === "customerReference" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.periodNumber && (
                                <TableHead
                                    className="cursor-pointer text-right"
                                    onClick={() => handleSort("periodNumber")}
                                >
                                    <div className="flex items-center justify-end gap-1">
                                        Period Number
                                        {sortColumn === "periodNumber" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.year && (
                                <TableHead
                                    className="cursor-pointer text-right"
                                    onClick={() => handleSort("year")}
                                >
                                    <div className="flex items-center justify-end gap-1">
                                        Year
                                        {sortColumn === "year" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.onDispute && (
                                <TableHead
                                    className="cursor-pointer"
                                    onClick={() => handleSort("onDispute")}
                                >
                                    <div className="flex items-center gap-1">
                                        On Dispute
                                        {sortColumn === "onDispute" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.subLedger && (
                                <TableHead
                                    className="cursor-pointer"
                                    onClick={() => handleSort("subLedger")}
                                >
                                    <div className="flex items-center gap-1">
                                        Sub Ledger
                                        {sortColumn === "subLedger" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                            {columnVisibility.orderNumber && (
                                <TableHead
                                    className="cursor-pointer"
                                    onClick={() => handleSort("orderNumber")}
                                >
                                    <div className="flex items-center gap-1">
                                        Order Number
                                        {sortColumn === "orderNumber" &&
                                            (sortDirection === "asc" ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            ))}
                                    </div>
                                </TableHead>
                            )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedTransactions.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={
                                        Object.values(columnVisibility).filter(Boolean).length +
                                        (enableSelection ? 1 : 0)
                                    }
                                    className="h-24 text-center"
                                >
                                    No transactions found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedTransactions.map((transaction) => (
                                <TableRow key={transaction.id}>
                                    {enableSelection && (
                                        <TableCell>
                                            <Checkbox
                                                checked={selectedRows.has(transaction.id)}
                                                onCheckedChange={(checked) =>
                                                    handleSelectRow(transaction.id, checked)
                                                }
                                            />
                                        </TableCell>
                                    )}
                                    {columnVisibility.referenceNumber && (
                                        <TableCell>
                                            <button
                                                onClick={() => onViewTransaction?.(transaction)}
                                                className="text-primary hover:underline font-medium"
                                            >
                                                {transaction.referenceNumber}
                                            </button>
                                        </TableCell>
                                    )}
                                    {columnVisibility.transactionDate && (
                                        <TableCell>{formatDateDDMMYYYY(transaction.transactionDate)}</TableCell>
                                    )}
                                    {columnVisibility.dueDate && (
                                        <TableCell>
                                            {transaction.dueDate ? formatDateDDMMYYYY(transaction.dueDate) : "-"}
                                        </TableCell>
                                    )}
                                    {showCustomerColumn && columnVisibility.customerName && (
                                        <TableCell>
                                            <button
                                                onClick={() => navigate(`/customers/${transaction.customerId}`)}
                                                className="text-primary hover:underline font-medium transition-colors"
                                                title={`View details for ${transaction.customerName}`}
                                            >
                                                {transaction.customerName}
                                            </button>
                                        </TableCell>
                                    )}
                                    {columnVisibility.transactionType && (
                                        <TableCell>
                                            <TransactionTypeBadge type={transaction.transactionType} />
                                        </TableCell>
                                    )}
                                    {columnVisibility.amount && (
                                        <TableCell className="text-right font-bold">
                                            {formatCurrency(transaction.amount, transaction.currency)}
                                        </TableCell>
                                    )}
                                    {columnVisibility.outstandingAmount && (
                                        <TableCell className="text-right font-medium">
                                            {transaction.outstandingAmount !== undefined
                                                ? formatCurrency(transaction.outstandingAmount, transaction.currency)
                                                : "-"}
                                        </TableCell>
                                    )}
                                    {columnVisibility.status && (
                                        <TableCell>
                                            <TransactionStatusBadge status={transaction.status} />
                                        </TableCell>
                                    )}
                                    {columnVisibility.description && (
                                        <TableCell className="max-w-[300px] truncate">
                                            {transaction.description}
                                        </TableCell>
                                    )}
                                    {columnVisibility.createdBy && (
                                        <TableCell>{transaction.createdBy}</TableCell>
                                    )}
                                    {columnVisibility.modifiedBy && (
                                        <TableCell>{transaction.modifiedBy || "-"}</TableCell>
                                    )}
                                    {columnVisibility.accountCode && (
                                        <TableCell>{transaction.accountCode || "-"}</TableCell>
                                    )}
                                    {columnVisibility.netAmount && (
                                        <TableCell className="text-right">
                                            {transaction.netAmount !== undefined
                                                ? formatCurrency(transaction.netAmount, transaction.currency)
                                                : "-"}
                                        </TableCell>
                                    )}
                                    {columnVisibility.vatAmount && (
                                        <TableCell className="text-right">
                                            {transaction.vatAmount !== undefined
                                                ? formatCurrency(transaction.vatAmount, transaction.currency)
                                                : "-"}
                                        </TableCell>
                                    )}
                                    {columnVisibility.customerReference && (
                                        <TableCell>{transaction.customerReference || "-"}</TableCell>
                                    )}
                                    {columnVisibility.periodNumber && (
                                        <TableCell className="text-right">
                                            {transaction.periodNumber || "-"}
                                        </TableCell>
                                    )}
                                    {columnVisibility.year && (
                                        <TableCell className="text-right">{transaction.year || "-"}</TableCell>
                                    )}
                                    {columnVisibility.onDispute && (
                                        <TableCell>
                                            {transaction.onDispute ? (
                                                <Badge variant="destructive" className="text-xs">
                                                    Yes
                                                </Badge>
                                            ) : (
                                                <span className="text-muted-foreground">No</span>
                                            )}
                                        </TableCell>
                                    )}
                                    {columnVisibility.subLedger && (
                                        <TableCell>{transaction.subLedger || "-"}</TableCell>
                                    )}
                                    {columnVisibility.orderNumber && (
                                        <TableCell>{transaction.orderNumber || "-"}</TableCell>
                                    )}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            {paginatedTransactions.length > 0 && (
                <div className="flex items-center justify-between px-2 py-4">
                    <div className="text-sm text-muted-foreground">
                        Showing {(currentPage - 1) * pageSize + 1} to{" "}
                        {Math.min(currentPage * pageSize, sortedTransactions.length)} of{" "}
                        {sortedTransactions.length} results
                    </div>
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Rows per page:</span>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-8 gap-1">
                                        {pageSize}
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    {[10, 25, 50, 100].map((size) => (
                                        <DropdownMenuItem
                                            key={size}
                                            onClick={() => {
                                                onPageSizeChange?.(size);
                                                onPageChange?.(1);
                                            }}
                                        >
                                            {size}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                                Page {currentPage} of {totalPages}
                            </span>
                            <div className="flex gap-1">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onPageChange?.(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => onPageChange?.(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
